// Client sync transport — connects this terminal to the LAN server (server/)
// so changes replicate live between counters, and keeps working offline.
//
// How it plugs in (no mutator knows this exists):
//   • It registers as the op *sink* (services/ops.ts): every local op is queued
//     in an offline OUTBOX and, when connected, POSTed to the server.
//   • It registers as the sequence *provider* (services/db.ts): bill/invoice/KOT
//     numbers are leased from the server in blocks so counters never collide;
//     offline it returns null and db falls back to the local counter.
//   • Inbound ops arrive over Server-Sent Events and go through applyRemoteOp,
//     which updates IndexedDB and fires COLLECTION_CHANGED so the UI refreshes.
//
// Connect/reconnect always reconciles by full snapshot: flush our outbox to the
// server, then pull the authoritative /state and adopt it. That makes commutative
// stock exact after any disconnection, at the cost of one snapshot per (re)connect
// — cheap at shop scale.
//
// Bootstrap rule: the FIRST terminal to connect to an empty server SEEDS it with
// its data; every later terminal ADOPTS the shared dataset. So set sync up on
// your main data device first.

import { Operation, registerOpSink, applyRemoteOp, snapshotLocal, applySnapshot } from './ops';
import * as db from './db';
import { cachedKey, cachedRawKey } from './idb';

// ---- Persisted config + state --------------------------------------------
const enabledCache = cachedRawKey('epos.sync.enabled', '0');
const urlCache = cachedRawKey('epos.sync.url', 'http://localhost:4560');
const tokenCache = cachedRawKey('epos.sync.token', '');
const outboxCache = cachedKey<Operation[]>('epos.sync.outbox', []);
const leasesCache = cachedKey<Record<string, { next: number; end: number }>>('epos.sync.leases', {});

const LEASE_SIZE = 50; // numbers reserved per lease — big enough to bill through a network blip
const RECONNECT_MS = 4000;

// ---- Live (in-memory) state ----------------------------------------------
let connected = false;
let connecting = false;
let flushing = false;
let lastSeq = 0;
let es: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setInterval> | null = null;
// Ids of ops WE sent to the server; used to ignore their echo on the SSE stream
// (we already applied them locally). Delta ops (stock) aren't idempotent, so this
// guard is essential.
const originatedIds = new Set<string>();

export function isSyncEnabled(): boolean {
  return enabledCache.get() === '1';
}

export function getServerUrl(): string {
  return (urlCache.get() || '').replace(/\/+$/, '');
}

export function getSyncToken(): string {
  return tokenCache.get() || '';
}

// Auth header sent on every request; empty object when no token is configured.
function authHeaders(): Record<string, string> {
  const token = getSyncToken();
  return token ? { 'x-epos-token': token } : {};
}

// EventSource can't send headers, so the token rides as a query param instead.
function tokenParam(): string {
  const token = getSyncToken();
  return token ? `&token=${encodeURIComponent(token)}` : '';
}

export interface SyncStatus {
  enabled: boolean;
  connected: boolean;
  pending: number; // ops waiting in the outbox
  url: string;
}

export const SYNC_STATUS_EVENT = 'sconnect:sync-status';

export function getSyncStatus(): SyncStatus {
  return { enabled: isSyncEnabled(), connected, pending: outboxCache.get().length, url: getServerUrl() };
}

function emitStatus(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SYNC_STATUS_EVENT, { detail: getSyncStatus() }));
  }
}

// ---- Small fetch helpers (with timeout) ----------------------------------
async function getJson(url: string): Promise<any> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 8000);
  try {
    const res = await fetch(url, { signal: ctrl.signal, headers: authHeaders() });
    if (!res.ok) throw new Error(`GET ${url} → ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(t);
  }
}

async function postJson(url: string, body: unknown): Promise<any> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 8000);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    if (!res.ok && res.status !== 409) throw new Error(`POST ${url} → ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(t);
  }
}

// ---- Outbox flush ---------------------------------------------------------
async function flush(base: string): Promise<void> {
  if (flushing) return;
  const outbox = outboxCache.get();
  if (outbox.length === 0) return;
  flushing = true;
  try {
    const batch = outbox.slice();
    const r = await postJson(`${base}/mutate`, { ops: batch });
    const acked: { id: string; seq: number }[] = r.acked || [];
    const ackedIds = new Set(acked.map((a) => a.id));
    acked.forEach((a) => originatedIds.add(a.id));
    // Drop acked ops; keep any that were appended mid-flight.
    await outboxCache.set(outboxCache.get().filter((o) => !ackedIds.has(o.id)));
  } finally {
    flushing = false;
  }
  emitStatus();
  if (connected && outboxCache.get().length > 0) flush(base).catch(() => {});
}

// ---- SSE stream -----------------------------------------------------------
function openStream(base: string): void {
  if (es) {
    es.close();
    es = null;
  }
  const stream = new EventSource(`${base}/events?lastEventId=${lastSeq}${tokenParam()}`);
  es = stream;
  stream.onmessage = (ev: MessageEvent) => {
    let op: Operation;
    try {
      op = JSON.parse(ev.data);
    } catch {
      return;
    }
    if (op.seq && op.seq > lastSeq) lastSeq = op.seq;
    if (originatedIds.has(op.id)) {
      // Our own change echoed back — already applied locally.
      originatedIds.delete(op.id);
      return;
    }
    applyRemoteOp(op).catch((e) => console.warn('applyRemoteOp failed', e));
  };
  stream.onerror = () => {
    // Tear down and let the reconnect loop do a full flush+resync, so no ops are
    // silently missed across the gap.
    if (es === stream) {
      stream.close();
      es = null;
      connected = false;
      emitStatus();
    }
  };
}

// ---- Connect / reconcile --------------------------------------------------
async function connect(): Promise<void> {
  if (connecting || connected || !isSyncEnabled()) return;
  connecting = true;
  try {
    const base = getServerUrl();
    const st = await getJson(`${base}/state`);
    const serverEmpty = (st.seq || 0) === 0 && Object.keys(st.collections || {}).length === 0;

    if (serverEmpty) {
      // First terminal online — seed the server from our data.
      await postJson(`${base}/seed`, { collections: await snapshotLocal(), counters: await db.getCounters() });
      lastSeq = 0;
    } else {
      // Push anything we changed while offline, then adopt the authoritative state.
      await flush(base);
      const fresh = await getJson(`${base}/state`);
      await applySnapshot(fresh.collections || {});
      lastSeq = fresh.seq || 0;
    }

    await flush(base);
    openStream(base);
    connected = true;
    emitStatus();
  } catch {
    connected = false;
    emitStatus();
  } finally {
    connecting = false;
  }
}

function startReconnectLoop(): void {
  if (reconnectTimer) return;
  reconnectTimer = setInterval(() => {
    if (isSyncEnabled() && !connected && !connecting) connect().catch(() => {});
  }, RECONNECT_MS);
}

function stopReconnectLoop(): void {
  if (reconnectTimer) {
    clearInterval(reconnectTimer);
    reconnectTimer = null;
  }
}

// ---- Sequence-number provider (leases) ------------------------------------
async function leaseSeq(kind: db.SeqKind): Promise<number | null> {
  const leases = leasesCache.get();
  const lease = leases[kind];
  if (lease && lease.next <= lease.end) {
    await leasesCache.set({ ...leases, [kind]: { next: lease.next + 1, end: lease.end } });
    return lease.next;
  }
  if (!connected) return null; // offline & lease used up → db falls back to local
  try {
    const r = await postJson(`${getServerUrl()}/lease`, { kind, count: LEASE_SIZE });
    await leasesCache.set({ ...leasesCache.get(), [kind]: { next: r.start + 1, end: r.end } });
    return r.start;
  } catch {
    return null;
  }
}

// ---- Op sink --------------------------------------------------------------
function onLocalOp(op: Operation): void {
  // Queue every local op; the flush ships it (and clears it on ack).
  outboxCache.set([...outboxCache.get(), op]).then(() => emitStatus());
  if (connected) flush(getServerUrl()).catch(() => {});
}

// ---- Wiring on/off --------------------------------------------------------
function attach(): void {
  registerOpSink(onLocalOp);
  db.registerSeqProvider(leaseSeq);
  startReconnectLoop();
}

function detach(): void {
  registerOpSink(null);
  db.registerSeqProvider(null);
  stopReconnectLoop();
  if (es) {
    es.close();
    es = null;
  }
  connected = false;
  emitStatus();
}

// Turn sync on/off or change the server URL (from Settings). Persists the choice
// and (re)connects or tears down accordingly.
export async function setSyncConfig(enabled: boolean, url: string, token = ''): Promise<void> {
  await enabledCache.set(enabled ? '1' : '0');
  await urlCache.set(url.trim().replace(/\/+$/, ''));
  await tokenCache.set(token.trim());
  detach();
  if (enabled) {
    attach();
    connect().catch(() => {});
  }
  emitStatus();
}

// Called once at boot (after initOps). Starts syncing if it was left enabled.
export function initSync(): void {
  if (isSyncEnabled()) {
    attach();
    connect().catch(() => {});
  }
  emitStatus();
}
